import {
  ehMotivoLiberacao,
  MOTIVO_LIBERACAO_LABEL,
} from '@/shared/constants/motivoLiberacaoSentenca';
import { formatDate } from '@/shared/formatters';

import type { PainelPublicacaoSentenca } from '../types/painelListTypes';

/**
 * Linha de motivo abaixo de "Publicar sentença" quando um prazo que segura a sentença está
 * correndo (G12). `null` quando a publicação não está travada por prazo — inclusive quando o
 * core não mandou o campo (versão anterior), para não travar a tela por falta de dado.
 *
 * @example motivoDaTravaPorPrazo({ liberada: false, prazosEmCurso: [purga], liberaEm })
 *   // 'Prazo de Purga da Mora (Despejo) até 26/10/2026 — a publicação libera depois do vencimento'
 */
export function motivoDaTravaPorPrazo(
  publicacao: PainelPublicacaoSentenca | undefined,
): string | null {
  if (!publicacao || publicacao.liberada || publicacao.prazosEmCurso.length === 0) return null;
  const { prazosEmCurso } = publicacao;
  const ultimoVencimento =
    publicacao.liberaEm ?? prazosEmCurso[prazosEmCurso.length - 1].vencimentoAt;
  // `vencimentoAt` é instante com hora, não coluna DATE: `formatDate` dá o dia de Brasília, e
  // `formatDataPrazo` leria meia-noite UTC como DATE (dia seguinte para um vencimento às 21:00 de
  // Brasília). O core escreve a mesma data na recusa com `formatarInstanteEmDataSp`.
  const ate = formatDate(ultimoVencimento);
  // Dois prazos do mesmo tipo (ex.: Purga reaberta) repetiriam o nome; a data já é a do último.
  const nomes = [...new Set(prazosEmCurso.map((p) => p.nome))];
  if (nomes.length === 1) {
    return `Prazo de ${nomes[0]} até ${ate} — a publicação libera depois do vencimento`;
  }
  return `Prazos em curso até ${ate}: ${nomes.join(', ')} — a publicação libera depois do último vencimento`;
}

/**
 * Linha do editor quando a Secretaria liberou a trava antes do vencimento (D-06: o árbitro vê
 * que a publicação destravou por decisão dela, e por quê). `null` sem liberação vigente e quando o
 * core é anterior ao campo — aí a tela segue só com `liberada`.
 *
 * Motivo `null` (liberação antiga) ou fora da lista fechada sai sem parênteses: um id cru do
 * enum na tela do árbitro seria pior do que omitir o rótulo.
 *
 * @example linhaLiberacaoSecretaria({ ..., liberacaoSecretaria: { liberadaEm, motivo: 'EXTINCAO' } })
 *   // 'Trava liberada pela Secretaria em 17/10/2026 (Pedido de extinção).'
 */
export function linhaLiberacaoSecretaria(
  publicacao: PainelPublicacaoSentenca | undefined,
): string | null {
  const liberacao = publicacao?.liberacaoSecretaria;
  if (!liberacao) return null;
  const dia = formatDate(liberacao.liberadaEm);
  const motivo = ehMotivoLiberacao(liberacao.motivo)
    ? ` (${MOTIVO_LIBERACAO_LABEL[liberacao.motivo]})`
    : '';
  return `Trava liberada pela Secretaria em ${dia}${motivo}.`;
}
